import { useState } from 'react';
import { useTranslations } from 'use-intl';
import {
  Body,
  Card,
  Eyebrow,
  PageHeader,
  Stack,
  useDelayedLoading,
} from '@leanin/ui';
import { SaveBar } from '../../form/SaveBar';
import {
  AdminAccessPolicyChoice,
  type AdminAccess,
} from './AdminAccessPolicyChoice';
import {
  AdminJoinPolicyChoice,
  type AdminJoinPolicy,
} from './AdminJoinPolicyChoice';
import {
  AdminPostFloorChoice,
  type AdminPostFloor,
} from './AdminPostFloorChoice';
import { AdminFormPageSkeleton } from './AdminFormPageSkeleton';
import { failureFromError } from '../../../lib/api-error';
import { useCircle, useNetwork } from '../../../lib/queries/groups';
import {
  useUpdateCircle,
  useUpdateNetwork,
} from '../../../lib/mutations/groups';
import { useSpace } from '../../../lib/queries/spaces';
import { useUpdateSpace } from '../../../lib/mutations/spaces';

// Composes the access-axis primitives into the canonical "Visibility &
// access" tab body. Circles, Networks, and Spaces share this — only the
// kind label and the underlying mutation differ. Topics, capacity,
// location, etc. each live on the kind's Overview / Members / Settings
// tab; this surface owns the three access axes: who can find the group
// (access), who can read its content (min read role), how members join
// (join policy).

export type GroupVisibilityKind = 'circle' | 'network' | 'space';

type Props = {
  readonly id: string;
  readonly kind: GroupVisibilityKind;
};

// Access options hidden per kind.
//
// Networks DO expose `unlisted`: a company Network wants to be reachable by
// the link its admins send out while staying out of the public directory, and
// that is exactly the tier. Hiding it here also made the value one-way — the
// platform-admin create flow offers all three, so a Network could be created
// `unlisted` and then never set back to it, with the Visibility tab rendering
// no card selected because the row's own value wasn't in the list.
//
// Circles still omit it: `network` covers the middle ground they actually
// need (visible to the parent Network, nobody else), and a Circle reachable
// by link-but-not-listed is the mode leaders read as "private" and get wrong.
//
// `network` is hidden for every kind here and added back by `CircleInner`
// for a Circle that actually has a parent Network: the tier scopes discovery
// TO that Network, so offering it without one would let a leader pick a
// setting that hides the Circle from everybody.
const ACCESS_OMIT: Record<GroupVisibilityKind, ReadonlyArray<AdminAccess>> = {
  circle: ['unlisted', 'network'],
  network: ['network'],
  space: ['network'],
};

// Networks and Spaces have no parent Network, so their PATCH contracts don't
// accept the `network` tier and `ACCESS_OMIT` never offers it. This narrows
// the shared `Loaded.save` value to what those contracts take; the `private`
// branch is unreachable, and errs toward the most restrictive tier if the
// omit table is ever changed without updating the contract.
function withoutNetworkTier(a: AdminAccess): Exclude<AdminAccess, 'network'> {
  return a === 'network' ? 'private' : a;
}

// Kinds whose join axis is fixed rather than chosen. Every Network is
// request-to-join: there is no invite-only path, so the join section renders
// a statement, and saves funnel the row to `'request'` (normalizing legacy
// rows on next save) rather than offering a picker.
const JOIN_LOCKED: Record<GroupVisibilityKind, boolean> = {
  circle: false,
  network: true,
  space: false,
};

// Kinds where the join policy is not surfaced at all. Groups (spaces) are
// always open — anyone can join — so showing a join section would be
// misleading. The underlying joinPolicy field is preserved but not editable.
const JOIN_HIDDEN: Record<GroupVisibilityKind, boolean> = {
  circle: false,
  network: false,
  space: true,
};

// Circle and Network content is members-only by product decision — no
// picker; the tab renders a statement instead. Spaces are the open,
// drop-in surface, so who-can-POST is a leader choice there ("post
// without joining" is a real shape for them); their read floor stays
// derived from the access tier server-side (`update-space.ts`).
const POST_FLOOR_EDITABLE: Record<GroupVisibilityKind, boolean> = {
  circle: false,
  network: false,
  space: true,
};

type Loaded = {
  readonly access: AdminAccess;
  readonly joinPolicy: AdminJoinPolicy;
  /** `null` for kinds whose update contract doesn't accept the post floor
   *  (circles, networks). */
  readonly minPostRole: AdminPostFloor | null;
  /** Overrides the kind's default `ACCESS_OMIT` entry when the row itself
   *  decides which tiers apply — today only a Circle's parent Network. */
  readonly accessOmit?: ReadonlyArray<AdminAccess>;
  readonly save: (next: {
    access: AdminAccess;
    joinPolicy: AdminJoinPolicy;
    minPostRole: AdminPostFloor;
  }) => Promise<void>;
  readonly isPending: boolean;
};

// One inner component per kind so each only mounts the hooks it actually
// needs. The outer component picks which inner to render; the inner runs
// its own fetch + mutation, normalizes the result into `Loaded`, and hands
// off to the shared form body.

export function GroupVisibilitySection({ id, kind }: Props) {
  if (kind === 'circle') return <CircleInner id={id} />;
  if (kind === 'network') return <NetworkInner id={id} />;
  return <SpaceInner id={id} />;
}

function CircleInner({ id }: { readonly id: string }) {
  const detailQuery = useCircle(id);
  const mutation = useUpdateCircle();
  const detail = detailQuery.data;
  const showSkeleton = useDelayedLoading(
    detail === undefined || detail.access === 'visitor',
  );
  if (showSkeleton) {
    return <AdminFormPageSkeleton cards={1} rowsPerCard={3} withAction />;
  }
  if (detail === undefined || detail.access === 'visitor') return null;
  const circle = detail.circle;
  const loaded: Loaded = {
    access: circle.access,
    joinPolicy: circle.joinPolicy,
    minPostRole: null,
    accessOmit:
      circle.parentGroupId === null
        ? ACCESS_OMIT.circle
        : ACCESS_OMIT.circle.filter((a) => a !== 'network'),
    isPending: mutation.isPending,
    save: async ({ access, joinPolicy }) => {
      await mutation.mutateAsync({
        circleId: circle.id,
        access,
        joinPolicy,
        expectedVersion: circle.version,
      });
    },
  };
  return <VisibilityForm kind="circle" loaded={loaded} />;
}

function NetworkInner({ id }: { readonly id: string }) {
  const detailQuery = useNetwork(id);
  const mutation = useUpdateNetwork();
  const network = detailQuery.data;
  const showSkeleton = useDelayedLoading(network === undefined);
  if (showSkeleton) {
    return <AdminFormPageSkeleton cards={1} rowsPerCard={3} withAction />;
  }
  if (network === undefined) return null;
  const loaded: Loaded = {
    access: network.access,
    // Networks are always request-to-join (see JOIN_LOCKED) — the join axis
    // isn't editable here and the network PATCH contract doesn't accept it,
    // so saves carry the access and content axes only.
    joinPolicy: 'request',
    minPostRole: null,
    isPending: mutation.isPending,
    save: async ({ access }) => {
      await mutation.mutateAsync({
        networkId: network.id,
        access: withoutNetworkTier(access),
      });
    },
  };
  return <VisibilityForm kind="network" loaded={loaded} />;
}

function SpaceInner({ id }: { readonly id: string }) {
  const detailQuery = useSpace(id);
  const mutation = useUpdateSpace();
  const space = detailQuery.data ?? null;
  const showSkeleton = useDelayedLoading(space === null);
  if (showSkeleton) {
    return <AdminFormPageSkeleton cards={1} rowsPerCard={3} withAction />;
  }
  if (space === null) return null;
  const loaded: Loaded = {
    access: space.access,
    joinPolicy: space.joinPolicy,
    minPostRole: space.minPostRole,
    isPending: mutation.isPending,
    save: async ({ access, minPostRole }) => {
      await mutation.mutateAsync({
        id: space.id,
        access: withoutNetworkTier(access),
        minPostRole,
      });
    },
  };
  return <VisibilityForm kind="space" loaded={loaded} />;
}

function VisibilityForm({
  kind,
  loaded,
}: {
  readonly kind: GroupVisibilityKind;
  readonly loaded: Loaded;
}) {
  const t = useTranslations('Group');
  const joinLocked = JOIN_LOCKED[kind];
  const joinHidden = JOIN_HIDDEN[kind];
  const postFloorEditable =
    POST_FLOOR_EDITABLE[kind] && loaded.minPostRole !== null;
  const [access, setAccess] = useState<AdminAccess>(loaded.access);
  const [joinPolicy, setJoinPolicy] = useState<AdminJoinPolicy>(
    joinLocked ? 'request' : loaded.joinPolicy,
  );
  const [postFloorChoice, setPostFloorChoice] = useState<AdminPostFloor>(
    loaded.minPostRole ?? 'member',
  );
  const [error, setError] = useState<string | null>(null);

  // A private group open to every poster is what the
  // `groups_post_floor_not_below_read_floor` CHECK forbids (a private
  // space's read floor derives to 'member'). Derived rather than written
  // back into the choice state: mutating the pick on access change would
  // leave a silent floor change armed for save if the leader flips to
  // `private` and back — deriving means leaving `private` restores
  // exactly what they picked.
  const postFloor: AdminPostFloor =
    access === 'private' && postFloorChoice === 'anyone'
      ? 'member'
      : postFloorChoice;

  const dirty =
    access !== loaded.access ||
    (!joinLocked && !joinHidden && joinPolicy !== loaded.joinPolicy) ||
    (postFloorEditable && postFloor !== loaded.minPostRole);

  const handleSave = async () => {
    if (!dirty) return;
    setError(null);
    try {
      await loaded.save({ access, joinPolicy, minPostRole: postFloor });
    } catch (err) {
      setError(
        failureFromError(err, t('admin.visibility.saveError', { kind })).message,
      );
    }
  };

  return (
    <Stack gap="lg">
      <PageHeader title={t('admin.visibility.title')} />

      <Card pad="default">
        <Stack gap="xl">
          <AdminAccessPolicyChoice
            value={access}
            onChange={setAccess}
            entityLabel={kind}
            omit={loaded.accessOmit ?? ACCESS_OMIT[kind]}
          />

          {kind !== 'space' && (
            <Stack gap="md">
              <Eyebrow tone="accent">
                {t('admin.visibility.whoCanSeeEyebrow', { kind })}
              </Eyebrow>
              <Body size="sm" color="mute">
                {t('admin.visibility.whoCanSeeBody', { kind })}
              </Body>
            </Stack>
          )}

          {postFloorEditable && (
            <AdminPostFloorChoice
              value={postFloor}
              onChange={setPostFloorChoice}
              entityLabel={kind}
              omitAnyone={access === 'private'}
            />
          )}

          {!joinHidden && (joinLocked ? (
            <Stack gap="md">
              <Eyebrow tone="accent">{t('admin.join.eyebrow')}</Eyebrow>
              <Body size="sm" color="mute">
                {t('admin.visibility.lockedBody', { kind })}
              </Body>
            </Stack>
          ) : (
            <AdminJoinPolicyChoice
              value={joinPolicy}
              onChange={setJoinPolicy}
              entityLabel={kind}
            />
          ))}
        </Stack>
      </Card>

      <SaveBar
        dirty={dirty}
        saving={loaded.isPending}
        error={error}
        onSave={handleSave}
      />
    </Stack>
  );
}
